import sourceUrl from "./Source.api";

const getHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
  "Content-Type": "application/json",
});

// --- Persona ---
export const getPersona = async () => {
  const res = await fetch(`${sourceUrl}/persona`, { headers: getHeaders() });
  return res.json();
};

export const setPersona = async (personaType) => {
  const res = await fetch(`${sourceUrl}/persona`, {
    method: "POST",
    headers: getHeaders(),
    body: JSON.stringify({ persona_type: personaType }),
  });
  return res.json();
};

export const switchPersona = async (personaType) => {
  const res = await fetch(`${sourceUrl}/persona/switch`, {
    method: "PUT",
    headers: getHeaders(),
    body: JSON.stringify({ persona_type: personaType }),
  });
  return res.json();
};

// --- SMB Businesses ---
export const listBusinesses = async () => {
  const res = await fetch(`${sourceUrl}/smb/businesses`, { headers: getHeaders() });
  return res.json();
};

export const createBusiness = async (data) => {
  const res = await fetch(`${sourceUrl}/smb/businesses`, {
    method: "POST",
    headers: getHeaders(),
    body: JSON.stringify(data),
  });
  return res.json();
};

export const getBusiness = async (businessId) => {
  const res = await fetch(`${sourceUrl}/smb/businesses/${businessId}`, { headers: getHeaders() });
  return res.json();
};

export const updateBusiness = async (businessId, data) => {
  const res = await fetch(`${sourceUrl}/smb/businesses/${businessId}`, {
    method: "PUT",
    headers: getHeaders(),
    body: JSON.stringify(data),
  });
  return res.json();
};

export const deleteBusiness = async (businessId) => {
  const res = await fetch(`${sourceUrl}/smb/businesses/${businessId}`, {
    method: "DELETE",
    headers: getHeaders(),
  });
  return res.json();
};

export const getOnboardingStatus = async () => {
  const res = await fetch(`${sourceUrl}/smb/onboarding/status`, { headers: getHeaders() });
  return res.json();
};

export const updateOnboardingStep = async (businessId, step) => {
  const res = await fetch(`${sourceUrl}/smb/businesses/${businessId}/onboarding/step`, {
    method: "PUT",
    headers: getHeaders(),
    body: JSON.stringify({ step }),
  });
  return res.json();
};

// --- Social Media ---
export const listSocialAccounts = async (businessId) => {
  const res = await fetch(`${sourceUrl}/smb/businesses/${businessId}/social/accounts`, { headers: getHeaders() });
  return res.json();
};

export const initSocialOAuth = async (businessId, platform, accountType) => {
  const res = await fetch(`${sourceUrl}/smb/businesses/${businessId}/social/connect`, {
    method: "POST",
    headers: getHeaders(),
    body: JSON.stringify({ platform, account_type: accountType }),
  });
  return res.json();
};

export const disconnectSocialAccount = async (businessId, accountId) => {
  const res = await fetch(`${sourceUrl}/smb/businesses/${businessId}/social/accounts/${accountId}`, {
    method: "DELETE",
    headers: getHeaders(),
  });
  return res.json();
};

// --- Content ---
export const generateStarterContent = (businessId) => {
  return fetch(`${sourceUrl}/smb/businesses/${businessId}/content/generate-starter`, {
    method: "POST",
    headers: getHeaders(),
  });
};

export const listContent = async (businessId, status = "", page = 1) => {
  const params = new URLSearchParams({ page, page_size: 20 });
  if (status) params.set("status", status);
  const res = await fetch(`${sourceUrl}/smb/businesses/${businessId}/content?${params}`, { headers: getHeaders() });
  return res.json();
};

export const updateContent = async (businessId, contentId, data) => {
  const res = await fetch(`${sourceUrl}/smb/businesses/${businessId}/content/${contentId}`, {
    method: "PUT",
    headers: getHeaders(),
    body: JSON.stringify(data),
  });
  return res.json();
};

export const approveContent = async (businessId, contentId) => {
  const res = await fetch(`${sourceUrl}/smb/businesses/${businessId}/content/${contentId}/approve`, {
    method: "PUT",
    headers: getHeaders(),
  });
  return res.json();
};

export const submitExpertReview = async (businessId, contentId) => {
  const res = await fetch(`${sourceUrl}/smb/businesses/${businessId}/content/${contentId}/submit-expert-review`, {
    method: "POST",
    headers: getHeaders(),
  });
  return res.json();
};

export const getContentCalendar = async (businessId, startDate, endDate) => {
  const params = new URLSearchParams();
  if (startDate) params.set("start_date", startDate);
  if (endDate) params.set("end_date", endDate);
  const res = await fetch(`${sourceUrl}/smb/businesses/${businessId}/content/calendar?${params}`, { headers: getHeaders() });
  return res.json();
};

export const scheduleContent = async (businessId, data) => {
  const res = await fetch(`${sourceUrl}/smb/businesses/${businessId}/content/schedule`, {
    method: "POST",
    headers: getHeaders(),
    body: JSON.stringify(data),
  });
  return res.json();
};

export const suggestPostingTimes = async (businessId) => {
  const res = await fetch(`${sourceUrl}/smb/businesses/${businessId}/content/suggest-times`, { headers: getHeaders() });
  return res.json();
};

// --- Website ---
export const generateWebsiteVariants = (businessId) => {
  return fetch(`${sourceUrl}/smb/businesses/${businessId}/website/generate-variants`, {
    method: "POST",
    headers: getHeaders(),
  });
};

export const listWebsiteVariants = async (businessId) => {
  const res = await fetch(`${sourceUrl}/smb/businesses/${businessId}/websites`, { headers: getHeaders() });
  return res.json();
};

export const selectWebsiteVariant = async (businessId, websiteId) => {
  const res = await fetch(`${sourceUrl}/smb/businesses/${businessId}/websites/${websiteId}/select`, {
    method: "PUT",
    headers: getHeaders(),
  });
  return res.json();
};

export const editWebsiteViaChat = (businessId, websiteId, editPrompt) => {
  return fetch(`${sourceUrl}/smb/businesses/${businessId}/websites/${websiteId}/edit`, {
    method: "POST",
    headers: getHeaders(),
    body: JSON.stringify({ edit_prompt: editPrompt }),
  });
};

export const requestDomainConnection = async (businessId, websiteId, domain) => {
  const res = await fetch(`${sourceUrl}/smb/businesses/${businessId}/website/domain/request`, {
    method: "POST",
    headers: getHeaders(),
    body: JSON.stringify({ website_id: websiteId, domain }),
  });
  return res.json();
};

export const getDomainStatus = async (businessId) => {
  const res = await fetch(`${sourceUrl}/smb/businesses/${businessId}/website/domain/status`, { headers: getHeaders() });
  return res.json();
};

// --- Leads ---
export const listLeads = async (businessId, classification = "", status = "", page = 1) => {
  const params = new URLSearchParams({ page, page_size: 20 });
  if (classification) params.set("classification", classification);
  if (status) params.set("status", status);
  const res = await fetch(`${sourceUrl}/smb/businesses/${businessId}/leads?${params}`, { headers: getHeaders() });
  return res.json();
};

export const createLead = async (businessId, data) => {
  const res = await fetch(`${sourceUrl}/smb/businesses/${businessId}/leads`, {
    method: "POST",
    headers: getHeaders(),
    body: JSON.stringify(data),
  });
  return res.json();
};

export const updateLead = async (businessId, leadId, data) => {
  const res = await fetch(`${sourceUrl}/smb/businesses/${businessId}/leads/${leadId}`, {
    method: "PUT",
    headers: getHeaders(),
    body: JSON.stringify(data),
  });
  return res.json();
};

export const deleteLead = async (businessId, leadId) => {
  const res = await fetch(`${sourceUrl}/smb/businesses/${businessId}/leads/${leadId}`, {
    method: "DELETE",
    headers: getHeaders(),
  });
  return res.json();
};

export const uploadLeadsCsv = async (businessId, file) => {
  const formData = new FormData();
  formData.append("csv", file);
  const res = await fetch(`${sourceUrl}/smb/businesses/${businessId}/leads/upload-csv`, {
    method: "POST",
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    body: formData,
  });
  return res.json();
};

export const getLeadStats = async (businessId) => {
  const res = await fetch(`${sourceUrl}/smb/businesses/${businessId}/leads/stats`, { headers: getHeaders() });
  return res.json();
};

// --- Dashboard ---
export const getDashboard = async (businessId) => {
  const res = await fetch(`${sourceUrl}/smb/businesses/${businessId}/dashboard`, { headers: getHeaders() });
  return res.json();
};

// --- Payment ---
export const createSmbCheckout = async (subscriptionType) => {
  const res = await fetch(`${sourceUrl}/payment/createCheckoutSession`, {
    method: "POST",
    headers: getHeaders(),
    body: JSON.stringify({ subscriptioType: subscriptionType }),
  });
  return res.json();
};
